// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useRef, useState } from "react";
import { APIService } from "@utils/apiService";

import { useAsgardeo } from "@asgardeo/react";
import { ProtectedRoute } from "@asgardeo/react-router";
import { BasicUserInfo, DecodedIDTokenPayload } from "@utils/types";

import {
  setUserAuthData,
  loadPrivileges,
  getUserProfileInfo,
} from "@slices/authSlice";

import { useAppDispatch } from "../slices/store";
import PreLoader from "@component/common/PreLoader";
import { loadInitialData } from "@slices/employeeSlice/employee";
import { fetchAppConfig } from "../slices/configSlice/config";

// Module-scoped (not per-render) so that many concurrent 401s, or repeated
// effect runs, only ever trigger ONE hidden-iframe silent sign-in / ONE full
// sign-in redirect, with every caller awaiting/reusing the same attempt.
let signInInFlight = false;
let silentSignInInFlight: Promise<boolean> | null = null;

const AuthGuard = (props: { children: React.ReactElement }) => {
  const [user, setUser] = useState<BasicUserInfo | null>(null);
  // Guards against constructing APIService more than once. The effect below
  // re-runs whenever isSignedIn changes, and a transient flicker (e.g. the
  // SDK's own on-demand refresh failing internally while our silent sign-in
  // recovery is still in flight) must NOT re-construct APIService — doing so
  // replaces its axios instance mid-recovery and orphans whatever retry was
  // already in progress.
  const apiServiceInitializedRef = useRef(false);

  const dispatch = useAppDispatch();

  const {
    isInitialized,
    isLoading,
    isSignedIn,
    getIdToken,
    getDecodedIdToken,
    signIn,
    signInSilently,
    signOut,
    user: asgardeoUser,
  } = useAsgardeo();

  // Full sign-in redirect, single-flighted so concurrent recovery attempts
  // don't each fire their own navigation. IMPORTANT: signIn() does not
  // reliably cause a visible page navigation here — observed in practice to
  // sometimes resolve its own promise and reconcile isSignedIn without ever
  // unloading the page. So this must NOT return a promise that hangs forever
  // "because a redirect is coming" — that leaves the original caller stuck
  // forever if no redirect actually happens. Instead, after signIn() settles,
  // check whether a token is now available and resolve with it; only reject
  // (never hang) if it genuinely isn't.
  const redirectToSignIn = async (): Promise<{ idToken: string }> => {
    if (!signInInFlight) {
      signInInFlight = true;
      try {
        await signIn();
      } finally {
        signInInFlight = false;
      }
    } else {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // If signIn() caused a real navigation, the page has already unloaded and
    // none of this runs. If we're still here, check whether the session
    // recovered anyway rather than leaving the caller stuck indefinitely.
    try {
      const idToken = await getIdToken();
      return { idToken };
    } catch {
      throw new Error("Unable to recover session: sign-in did not complete");
    }
  };

  // Before bouncing the whole tab to a full sign-in redirect, try a silent,
  // hidden-iframe re-authentication (prompt=none). If the Asgardeo SSO
  // session is still alive, this mints a fresh token with no visible
  // navigation; only a genuinely dead session falls through to
  // redirectToSignIn. Single-flighted for the same reason as
  // redirectToSignIn above.
  const trySilentSignIn = (): Promise<boolean> => {
    if (!silentSignInInFlight) {
      silentSignInInFlight = Promise.resolve(signInSilently())
        .then((result) => Boolean(result))
        .catch(() => false)
        .finally(() => {
          silentSignInInFlight = null;
        });
    }
    return silentSignInInFlight;
  };

  const refreshToken = async (): Promise<{ idToken: string }> => {
    // The SDK may have already refreshed the token in the background by the
    // time this fires (or a concurrent caller's recovery already landed) —
    // re-read before assuming it's genuinely dead.
    try {
      const idToken = await getIdToken();
      return { idToken };
    } catch {
      // fall through to trySilentSignIn below
    }

    const silentSignInResult = await trySilentSignIn();

    if (silentSignInResult) {
      try {
        const idToken = await getIdToken();
        // Resolve normally first — this lets APIService write the fresh
        // token and rebuild its interceptor, and lets the request that
        // triggered this recovery actually complete. window.location.reload()
        // doesn't halt JS execution immediately, so scheduling it here (after
        // resolving, not instead of resolving) gives a clean full restart
        // shortly after, without ever blocking a promise that other callers
        // are single-flighted against.
        setTimeout(() => window.location.reload(), 0);
        return { idToken };
      } catch {
        return redirectToSignIn();
      }
    }

    return redirectToSignIn();
  };

  useEffect(() => {
    // isLoading stays true for the SDK's own entire callback code-exchange
    // (it deliberately holds isLoading while the URL still carries the OAuth
    // `code`/`state` params — see @asgardeo/react's AsgardeoProvider). Gating
    // on isInitialized alone is NOT enough: isInitialized can flip true from
    // a wholly separate check while that exchange is still in flight, and
    // firing our own signIn() during that window races the SDK's one-time-use
    // PKCE code_verifier against its own in-progress exchange — observed as a
    // *cancelled* (not blocked/CORS) token-endpoint request, most reliably in
    // Safari due to its slower cross-site fetch handshake giving the race a
    // bigger window. Waiting for isLoading to clear avoids the race entirely.
    if (!isInitialized || isLoading) return;

    if (!isSignedIn) {
      // Only force a sign-in redirect on first load (never successfully
      // loaded before). Once the app has loaded once, isSignedIn can flicker
      // false transiently during a background token recovery that
      // refreshToken() is already handling silently — redirecting here too
      // would race a visible sign-in redirect against that silent recovery,
      // defeating the point of it. redirectToSignIn() can reject (if
      // signIn() didn't cause a navigation and the session still isn't
      // recoverable afterward), so it must be caught here too.
      //
      // Note: ProtectedRoute (below) is deliberately given a `fallback`, so
      // it never reaches its own internal auto-signIn branch — that branch
      // unconditionally throws an "AsgardeoRuntimeError" right after kicking
      // off signIn() (confirmed in @asgardeo/react-router@2.0.0's compiled
      // source: none of its signIn-triggering branches return before falling
      // through to a final `throw`). Without a `fallback`/`redirectTo`, that
      // throw fires on every unauthenticated render, and each catch-and-remount
      // re-triggers signIn() again — a request storm. This effect is what
      // actually drives sign-in instead.
      if (!user) {
        redirectToSignIn().catch(() => {});
      }
      return;
    }

    if (apiServiceInitializedRef.current) {
      return;
    }
    apiServiceInitializedRef.current = true;

    (async () => {
      const idToken = await getIdToken();
      const decodedIdToken = (await getDecodedIdToken()) as unknown as DecodedIDTokenPayload;
      // App.tsx sets preferences.user.fetchUserProfile to false, so the SDK
      // never populates its own `user` object for this tenant (asgardeoUser
      // stays null). Build userInfo from the decoded ID token (always
      // available, no extra request) instead, letting asgardeoUser's fields
      // take precedence for whichever platforms/tenants do populate it.
      const userInfo: BasicUserInfo = {
        ...(decodedIdToken as unknown as BasicUserInfo),
        ...((asgardeoUser as BasicUserInfo) ?? {}),
      };

      dispatch(
        setUserAuthData({
          userInfo: userInfo,
          idToken: idToken,
          decodedIdToken: decodedIdToken,
        })
      );

      setUser(userInfo);
      new APIService(idToken, refreshToken);
      Promise.all([
        dispatch(loadPrivileges()),
        dispatch(getUserProfileInfo()),
        dispatch(loadInitialData()),
        dispatch(fetchAppConfig()),
      ]);
    })().catch(() => {
      apiServiceInitializedRef.current = false;
      signOut().catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isLoading, isSignedIn]);

  const loader = <PreLoader isLoading={true} message="" />;

  return (
    <ProtectedRoute
      loader={loader}
      // Gated on "never successfully loaded" (the `user` state) rather than
      // the raw isSignedIn flag: isSignedIn can flicker false transiently
      // during a background token recovery, and falling back to the loader
      // here would unmount the entire app tree for what should be an
      // invisible background refresh. Once loaded once, trust refreshToken()'s
      // own silent recovery instead of tearing the app down on every flicker.
      fallback={user ? props.children : loader}
    >
      {props.children}
    </ProtectedRoute>
  );
};

export default AuthGuard;
